// @flow
import React, {useRef, useEffect, useMemo, type Element} from 'react'
import classNames from 'classnames'
import SystemIcon from 'components/ui/icons/HammerAndWrench'
import Markdown from 'components/ui/Markdown'
import Message from '../Message'
import {type MessageType} from '../MessageType'
import {visibleTypes} from '../Message/renderableMessage'
import styles from './index.scss'

type Props = {
  messages: Array<MessageType>,
  ui: any,
  debugMode?: boolean,
  messagesPath: string,
};

function MessagesArea({messages, ui, debugMode = false, messagesPath}: Props): Element<'div'> {
  const messagesArea = useRef<?HTMLDivElement>(null)

  useEffect(() => {
    // scroll down
    if (messagesArea.current)
      messagesArea.current.scrollTop = messagesArea.current.scrollHeight
  }, [])

  const initialSystemMessages = []
  const restOfMessages = []
  let seenNonSystemMessage = false
  let filteredMsgs = messages

  if (!debugMode) {
    filteredMsgs = filteredMsgs.filter((msg) => {
      const hasVisibleRole = visibleTypes.includes(msg.role)
      const isFunctionCall = msg.role === 'assistant' && msg.function_call
      const hiddenError = msg.error_message && !msg.user_visible
      return hasVisibleRole && !isFunctionCall && !hiddenError
    })
  }

  filteredMsgs.forEach((message) => {
    if (!debugMode && !visibleTypes.includes(message.role))
      return

    if (message.role === 'system' && !seenNonSystemMessage) {
      initialSystemMessages.push(message)
    } else {
      seenNonSystemMessage = true
      restOfMessages.push(message)
    }
  })

  const lastMessage = restOfMessages[restOfMessages.length - 1]

  const images = useMemo(
    () => ({
      assistant: ui.frogbot ? (
        <img
          src={ui.frogbot.avatarUrl}
          alt={ui.frogbot.name}
          className={styles.avatar}
        />
      ) : null,
      user: ui.person ? (
        <img
          src={ui.person.avatarUrl}
          alt={ui.person.name}
          className={styles.avatar}
        />
      ) : null,
      system: <SystemIcon alt="system" className={styles.avatar} />,
    }),
    [ui.frogbot, ui.person],
  )

  const renderMessage = (message) => {
    const isLastMessage = message === lastMessage
    return (
      <div key={message.id} className={styles.message}>
        <Message
          message={message}
          messagesPath={messagesPath}
          images={images}
          isLastMessage={isLastMessage}
        />
      </div>
    )
  }

  return (
    <div className={styles.messages} ref={messagesArea}>
      {initialSystemMessages.map((message) => renderMessage(message))}

      {ui.frogbot && (
        <div className={classNames(styles.message, styles.assistant)}>
          <img
            alt={ui.frogbot.name}
            className={styles.avatar}
            src={ui.frogbot.avatarUrl}
          />
          <div className={styles.text}>
            <Markdown source={I18n.t('ai.chat.disclaimer')} />
            <p>{I18n.t('ai.chat.how_can_i_help')}</p>
          </div>
        </div>
      )}

      {restOfMessages.map((message) => renderMessage(message))}
    </div>
  )
}

export type {MessageType}
export default MessagesArea
